import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Row,
  Space,
  Tag,
  Typography,
  Input,
  Spin,
  message,
  Modal,
  notification,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import Form from '../../components/Form';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import { CandidateTagCollection } from '../../types';

interface CandidateTagsFormValues {
  name: string;
  candidateTags: CandidateTagCollection[];
}

const CandidateTags = () => {
  const candidateTagsPages = useInfiniteAPIQuery('/candidate-tags/');
  const candidateTags = usePrefetchPages(candidateTagsPages, (data) => data.results);

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const addMutation = useAPIMutation(
    async ({ name }: { name: string }, api) => {
      await api.post('/candidate-tags/', { name });
    },
    {
      onSuccess: async () => {
        form.setFieldValue('name', '');
        message.success('Tag adicionada com sucesso');
        queryClient.invalidateQueries(['/candidate-tags/']);
      },
      onError: (err, variables, context) => {
        if (
          err.response?.status === 400 &&
          err.response?.data?.name &&
          err.response.data.name.length > 0 &&
          err.response.data.name[0] === 'candidate tag com este name já existe.'
        ) {
          return notification.error({ message: 'Uma tag com esse nome já foi cadastrada.' });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  const removeMutation = useAPIMutation(
    async ({ id }: { id: number }, api) => {
      await api.delete(`/candidate-tags/${id}/`);
    },
    {
      onSuccess: () => {
        message.success('Tag removida com sucesso');
        queryClient.invalidateQueries(['/candidate-tags/']);
      },
    }
  );

  const nameValue = Form.useWatch('name', form);

  const addTag = (name: string) => {
    if (!nameValue) {
      return;
    }

    addMutation.mutate({ name });
  };

  const showConfirm = (id: number) => {
    Modal.confirm({
      title: 'Confirmar exclusão',
      content: (
        <Typography.Text>
          Ao apagar essa tag ela será excluída das opções de cadastro e também de{' '}
          <Typography.Text strong>todos os candidatos</Typography.Text>.
        </Typography.Text>
      ),
      okText: 'Apagar',
      onOk: () => removeMutation.mutate({ id }),
      okButtonProps: {
        loading: removeMutation.isLoading,
        danger: true,
      },
      cancelText: 'Cancelar',
      onCancel: () => {
        form.resetFields();
      },
    });
  };

  return (
    <Spin spinning={candidateTagsPages.isLoading}>
      <Helmet>
        <title>Tags dos Candidatos | Configurações | Recrutamento e Seleção</title>
      </Helmet>
      <Form<CandidateTagsFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          name: '',
          candidateTags: candidateTags || [],
        }}
      >
        <Form.Legend>Configuração de tags nos candidatos:</Form.Legend>
        <Space direction="vertical" size="middle">
          <Typography.Text>
            Configure as tags que poderão ser inseridas no perfil dos candidatos para separá-los por
            categorias ou características específicas
          </Typography.Text>
          <Row />
          <Space.Compact>
            <Form.Item name="name" noStyle>
              <Input
                placeholder="Adicionar tag"
                onPressEnter={() => addTag(nameValue)}
                disabled={addMutation.isLoading}
              />
            </Form.Item>
            <Button onClick={() => addTag(nameValue)}>
              {addMutation.isLoading ? <LoadingOutlined spin /> : <PlusOutlined />}
            </Button>
          </Space.Compact>
          <Form.List name="candidateTags">
            {(fields) => (
              <Space direction="vertical" size="middle">
                <Form.Row>
                  <Form.Col>
                    {fields.map(({ key, name }) => (
                      <Tag
                        key={key}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          showConfirm(form.getFieldValue(['candidateTags', name]).id);
                        }}
                      >
                        {form.getFieldValue(['candidateTags', name]).name}
                      </Tag>
                    ))}
                  </Form.Col>
                </Form.Row>
              </Space>
            )}
          </Form.List>
        </Space>
      </Form>
    </Spin>
  );
};

export default CandidateTags;
